import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { Input, Modal } from 'antd';
import type { InputRef } from 'antd';
import { type FC, type PropsWithChildren, useEffect, useRef } from 'react';
import { useControlled } from '../../../lib/customHooks';
import { IconSearch } from '../Icons';
import { LoadingIndicator } from '../LoadingIndicator';
import { Center } from '../MetaComponent';
import { ErrorMessage } from './ErrorMessage';
import { PseudoInput } from './PseudoInput';

interface Props {
  show?: boolean;
  fetching?: boolean;
  errorMessage?: string;
  query: string;
  onQueryChange: (query: string) => void;
  children?: Array<React.ReactNode>;
  size?: 'small' | 'large';
  reactOnHotkey?: boolean;
}

export const AdvancedSearchBar: FC<PropsWithChildren<Props>> = ({
  show,
  fetching = false,
  errorMessage = '',
  size = 'large',
  query,
  onQueryChange,
  children,
  reactOnHotkey = true,
}) => {
  const [showControlled, setShow] = useControlled({
    controlledValue: show,
    initialValue: false,
    name: 'show',
  });
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (!showControlled) return;
    inputRef.current?.focus({ cursor: 'start' });
  }, [showControlled]);

  return (
    <>
      <PseudoInput
        onClick={() => setShow(true)}
        text={size === 'large' ? 'Ask for help...' : 'Help'}
        hotkeyActive={reactOnHotkey}
      />
      <SearchModal
        open={showControlled}
        onCancel={() => setShow(false)}
        closable={false}
        footer={null}
      >
        <SearchInput
          variant="borderless"
          placeholder="Ask or search..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          prefix={<IconSearch color={RacemapColors.PaleBlue} />}
          size="large"
          ref={inputRef}
        />
        {isNotEmptyString(errorMessage) ? (
          <ErrorMessage message={errorMessage} />
        ) : fetching ? (
          <Center>
            <LoadingIndicator borderless text="Fetching Search Results..." />
          </Center>
        ) : (
          children
        )}
      </SearchModal>
    </>
  );
};

const SearchModal = styled(Modal)`
.ant-modal-content {
    padding: 5px 10px;
}
`;

const SearchInput = styled(Input)`
    box-shadow: none !important;
    
    input {
        color: rgba(0, 0, 0, 0.85);
    }
`;
